/**
 * CablePathDrawingOverlay
 *
 * Overlay component for drawing cable paths as polylines.
 * Handles:
 * - Click to add vertices (turning points)
 * - Context menu for adding junction boxes or ending path
 * - Live length preview
 * - Snap to nearby junctions
 * - Escape to cancel
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppStore, useCablePathDrawingState } from '@/stores/appStore';
import { useFloorPlanContext } from '../../FloorPlanProvider';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { calculateDistance, pixelsToUnits, formatMeasurement } from '@/utils/scaleCalculations';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import type { Point } from '@/types/common';
import type { CableJunction } from '@/types/cablePaths';
import { DrawingContextMenu } from './DrawingContextMenu';

interface CablePathDrawingOverlayProps {
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
}

// Snap threshold in pixels (before scaling)
const SNAP_THRESHOLD = 20;

export function CablePathDrawingOverlay({
  containerRef,
  zoom,
  position,
}: CablePathDrawingOverlayProps) {
  // Local state for cursor position and shift key
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const [isShiftHeld, setIsShiftHeld] = useState(false);

  // Get floor plan context
  const { floorPlan, scaleData, data } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Get default junction scale from floor plan metadata
  const markerSizes = data.floorPlanMetadata?.marker_sizes as {
    junction_default_scale?: number;
  } | null;
  const defaultJunctionScale = markerSizes?.junction_default_scale ?? 1.0;

  // Use ref to track current default scale to avoid stale closure issues
  // Update synchronously during render (not in useEffect) to guarantee
  // the ref is always current before any callbacks can run
  const defaultJunctionScaleRef = useRef(defaultJunctionScale);
  defaultJunctionScaleRef.current = defaultJunctionScale;

  // Get junctions for snapping and creating
  const { junctions, junctionCoordMap, createJunction } =
    useCableJunctionsStandardized(floorPlanId);

  // Get path operations
  const { createPath } = useCablePathsStandardized(floorPlanId, junctionCoordMap, scaleData);

  // Track pending operations
  const [isProcessing, setIsProcessing] = useState(false);

  // Get drawing state from store
  const drawingState = useCablePathDrawingState();
  const {
    isDrawing,
    currentPoints,
    startJunctionId,
    snapToJunctionId,
    liveLength,
    showContextMenu,
    contextMenuScreenPosition,
    lastClickedPoint,
  } = drawingState;

  // Get store actions
  const activeMode = useAppStore(state => state.ui.toolbar.activeMode);
  const addCablePathPoint = useAppStore(state => state.addCablePathPoint);
  const updateCablePathLiveLength = useAppStore(state => state.updateCablePathLiveLength);
  const setSnapToJunction = useAppStore(state => state.setSnapToJunction);
  const finishCablePathDrawing = useAppStore(state => state.finishCablePathDrawing);
  const cancelCablePathDrawing = useAppStore(state => state.cancelCablePathDrawing);
  const startCablePathDrawing = useAppStore(state => state.startCablePathDrawing);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const showDrawingContextMenu = useAppStore(state => state.showDrawingContextMenu);
  const hideDrawingContextMenu = useAppStore(state => state.hideDrawingContextMenu);
  const selectEntity = useAppStore(state => state.selectEntity);

  // Check if we're in cable path drawing mode
  const isCablePathMode = activeMode === 'cable-path-drawing';

  // Convert screen coordinates to floor plan coordinates
  const screenToFloorPlanCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;
      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Find nearest junction within snap threshold
  const findNearestJunction = useCallback(
    (point: Point): CableJunction | null => {
      if (!junctions || junctions.length === 0) return null;

      const snapThreshold = SNAP_THRESHOLD / zoom;
      let nearestJunction: CableJunction | null = null;
      let minDistance = Infinity;

      for (const junction of junctions) {
        const distance = calculateDistance(point, { x: junction.x, y: junction.y });
        if (distance < snapThreshold && distance < minDistance) {
          minDistance = distance;
          nearestJunction = junction;
        }
      }

      return nearestJunction;
    },
    [junctions, zoom],
  );

  // Get the last point for shift-constraint calculations
  const getLastPoint = useCallback((): Point | null => {
    if (currentPoints.length > 0) {
      return currentPoints[currentPoints.length - 1];
    }
    if (startJunctionId && junctionCoordMap) {
      const startJunction = junctionCoordMap.get(startJunctionId);
      if (startJunction) {
        return { x: startJunction.x, y: startJunction.y };
      }
    }
    return null;
  }, [currentPoints, startJunctionId, junctionCoordMap]);

  // Apply shift-key constraint for horizontal/vertical lines
  const applyShiftConstraint = useCallback(
    (point: Point, shiftKey: boolean): Point => {
      if (!shiftKey) return point;

      const lastPoint = getLastPoint();
      if (!lastPoint) return point;

      const dx = Math.abs(point.x - lastPoint.x);
      const dy = Math.abs(point.y - lastPoint.y);

      if (dx > dy) {
        // Constrain to horizontal line
        return { x: point.x, y: lastPoint.y };
      } else {
        // Constrain to vertical line
        return { x: lastPoint.x, y: point.y };
      }
    },
    [getLastPoint],
  );

  // Calculate total path length including current cursor position
  const calculateTotalLength = useCallback(
    (points: Point[], cursorPoint?: Point | null): number => {
      const allPoints = [...points];

      // Add start junction if present
      if (startJunctionId && junctionCoordMap) {
        const startJunction = junctionCoordMap.get(startJunctionId);
        if (startJunction && allPoints.length === 0) {
          allPoints.unshift({ x: startJunction.x, y: startJunction.y });
        }
      }

      // Add cursor position
      if (cursorPoint) {
        allPoints.push(cursorPoint);
      }

      if (allPoints.length < 2) return 0;

      let totalPixels = 0;
      for (let i = 0; i < allPoints.length - 1; i++) {
        totalPixels += calculateDistance(allPoints[i], allPoints[i + 1]);
      }

      return scaleData ? pixelsToUnits(totalPixels, scaleData) : totalPixels;
    },
    [startJunctionId, junctionCoordMap, scaleData],
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isCablePathMode) return;

      // Track shift key state
      setIsShiftHeld(e.shiftKey);

      let point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      // Apply shift constraint for horizontal/vertical lines (only when drawing)
      if (isDrawing && e.shiftKey) {
        point = applyShiftConstraint(point, true);
      }

      setCursorPos(point);

      // Check for junction snapping (skip if shift is held for straight lines)
      const nearestJunction = e.shiftKey ? null : findNearestJunction(point);
      setSnapToJunction(nearestJunction?.id || null);

      // Update live length if drawing
      if (isDrawing) {
        const snapPoint = nearestJunction ? { x: nearestJunction.x, y: nearestJunction.y } : point;
        const length = calculateTotalLength(currentPoints, snapPoint);
        updateCablePathLiveLength(length);
      }
    },
    [
      isCablePathMode,
      isDrawing,
      screenToFloorPlanCoords,
      findNearestJunction,
      setSnapToJunction,
      currentPoints,
      calculateTotalLength,
      updateCablePathLiveLength,
      applyShiftConstraint,
    ],
  );

  // Handle click to add point
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!isCablePathMode) return;

      // Check if clicking on interactive elements
      const target = e.target as HTMLElement;

      // Always let context menu and buttons handle their own clicks
      if (target.closest('[data-context-menu]') || target.closest('button')) {
        return;
      }

      const isInteractiveElement =
        target.closest('[data-junction-id]') ||
        target.closest('[data-path-id]') ||
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]');

      if (isInteractiveElement && !isDrawing) {
        return; // Let the element handle the click
      }

      // If context menu is visible and clicking on canvas, dismiss it
      if (showContextMenu) {
        hideDrawingContextMenu();
      }

      let point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      e.preventDefault();
      e.stopPropagation();

      // Apply shift constraint for horizontal/vertical lines (only when drawing)
      if (isDrawing && e.shiftKey) {
        point = applyShiftConstraint(point, true);
      }

      // Get snap target (skip if shift is held)
      const nearestJunction = e.shiftKey ? null : findNearestJunction(point);
      const snapPoint = nearestJunction ? { x: nearestJunction.x, y: nearestJunction.y } : point;

      if (!isDrawing) {
        // Start drawing
        logger.log('Starting cable path drawing', { startJunction: nearestJunction?.id });
        startCablePathDrawing(nearestJunction?.id || null);

        // If not starting from a junction, add the first point
        if (!nearestJunction) {
          addCablePathPoint(snapPoint);
        }
      } else {
        // Add vertex to path
        logger.log('Adding cable path vertex', {
          point: snapPoint,
          isJunction: !!nearestJunction,
          shiftKey: e.shiftKey,
        });
        addCablePathPoint(snapPoint);

        // Show context menu at this point
        showDrawingContextMenu({ x: e.clientX, y: e.clientY }, snapPoint);
      }
    },
    [
      isCablePathMode,
      isDrawing,
      showContextMenu,
      screenToFloorPlanCoords,
      findNearestJunction,
      startCablePathDrawing,
      addCablePathPoint,
      showDrawingContextMenu,
      hideDrawingContextMenu,
      applyShiftConstraint,
    ],
  );

  // Create a path segment (used by both handleAddBox and handleEnd)
  const createPathSegment = useCallback(
    async (endJunctionId: string | null, endPoint: Point | null): Promise<string | null> => {
      // Determine start point for potential junction creation
      const startPoint = currentPoints.length > 0 ? currentPoints[0] : null;

      // Auto-create start junction if path doesn't start from one
      let actualStartJunctionId = startJunctionId;
      if (!startJunctionId && startPoint) {
        logger.log('Auto-creating start junction at', startPoint);
        const newStartJunction = await createJunction(
          {
            floor_plan_id: floorPlanId,
            x: startPoint.x,
            y: startPoint.y,
            scale: defaultJunctionScaleRef.current,
          },
          { showSuccessToast: false },
        );
        actualStartJunctionId = (newStartJunction as CableJunction).id;
        logger.log('Created start junction:', actualStartJunctionId);
      }

      // Auto-create end junction if needed
      let actualEndJunctionId = endJunctionId;
      if (!endJunctionId && endPoint) {
        logger.log('Auto-creating end junction at', endPoint);
        const newEndJunction = await createJunction(
          {
            floor_plan_id: floorPlanId,
            x: endPoint.x,
            y: endPoint.y,
            scale: defaultJunctionScaleRef.current,
          },
          { showSuccessToast: false },
        );
        actualEndJunctionId = (newEndJunction as CableJunction).id;
        logger.log('Created end junction:', actualEndJunctionId);
      }

      // Remove start point from intermediate points if it's now a junction
      let intermediatePoints = [...currentPoints];
      if (!startJunctionId && startPoint) {
        intermediatePoints = intermediatePoints.slice(1);
      }
      // Remove the last point if it's becoming the end junction
      if (intermediatePoints.length > 0 && endPoint) {
        const lastPoint = intermediatePoints[intermediatePoints.length - 1];
        if (lastPoint.x === endPoint.x && lastPoint.y === endPoint.y) {
          intermediatePoints = intermediatePoints.slice(0, -1);
        }
      }

      // Create the path
      const path = await createPath(
        {
          floor_plan_id: floorPlanId,
          start_junction_id: actualStartJunctionId,
          end_junction_id: actualEndJunctionId,
          points: intermediatePoints,
          length_real: liveLength,
          length_unit: scaleData?.unit || 'ft',
          path_type: 'conduit',
          route: 'ceiling',
          cables: [],
          vertical_drop_start: 10,
          vertical_drop_end: 10,
          vertical_drop_unit: scaleData?.unit || 'ft',
        },
        { showSuccessToast: false },
      );

      logger.log('Cable path created:', (path as { id: string }).id);
      return actualEndJunctionId;
    },
    [
      currentPoints,
      startJunctionId,
      createJunction,
      createPath,
      floorPlanId,
      liveLength,
      scaleData,
    ],
  );

  // Handle "+ Box" button - create junction at current point and continue drawing
  const handleAddBox = useCallback(async () => {
    if (!lastClickedPoint || isProcessing) return;

    setIsProcessing(true);
    hideDrawingContextMenu();

    try {
      // Check if we have any points to create a path
      if (currentPoints.length === 0 && !startJunctionId) {
        // No path to create, just create a junction and start from it
        const newJunction = await createJunction(
          {
            floor_plan_id: floorPlanId,
            x: lastClickedPoint.x,
            y: lastClickedPoint.y,
            scale: defaultJunctionScaleRef.current,
          },
          { showSuccessToast: false },
        );
        const junctionId = (newJunction as CableJunction).id;

        // Start new path from this junction
        startCablePathDrawing(junctionId);
        return;
      }

      // Create path segment ending at a new junction
      const newJunctionId = await createPathSegment(null, lastClickedPoint);

      if (newJunctionId) {
        // Start new path from the new junction
        startCablePathDrawing(newJunctionId);
      }
    } catch (error) {
      logger.error('Failed to add junction box:', error);
      toast.error('Failed to add junction box');
    } finally {
      setIsProcessing(false);
    }
  }, [
    lastClickedPoint,
    isProcessing,
    currentPoints,
    startJunctionId,
    createJunction,
    createPathSegment,
    floorPlanId,
    hideDrawingContextMenu,
    startCablePathDrawing,
  ]);

  // Handle "End" button - finish the path at current point
  const handleEnd = useCallback(async () => {
    logger.log('handleEnd called', {
      lastClickedPoint,
      isProcessing,
      currentPointsLength: currentPoints.length,
      startJunctionId,
    });

    if (!lastClickedPoint) {
      logger.warn('handleEnd: No lastClickedPoint, returning');
      return;
    }
    if (isProcessing) {
      logger.warn('handleEnd: Already processing, returning');
      return;
    }

    setIsProcessing(true);
    hideDrawingContextMenu();

    try {
      // Need at least one point or a start junction to create a path
      if (currentPoints.length === 0 && !startJunctionId) {
        logger.warn('handleEnd: No points and no start junction');
        cancelCablePathDrawing();
        toast.info('No path to create');
        return;
      }

      logger.log('handleEnd: Creating path segment', { currentPoints, lastClickedPoint });

      // Create final path segment
      const endJunctionId = await createPathSegment(null, lastClickedPoint);

      logger.log('handleEnd: Path segment created, endJunctionId:', endJunctionId);

      if (endJunctionId) {
        // Select the last junction and open drawer
        selectEntity('cableJunction', endJunctionId);
        finishCablePathDrawing();
        setActiveDrawer('cableJunction');
      } else {
        logger.warn('handleEnd: No endJunctionId returned');
        cancelCablePathDrawing();
      }
    } catch (error) {
      logger.error('Failed to end path:', error);
      toast.error('Failed to end path');
      cancelCablePathDrawing();
    } finally {
      setIsProcessing(false);
    }
  }, [
    lastClickedPoint,
    isProcessing,
    currentPoints,
    startJunctionId,
    createPathSegment,
    hideDrawingContextMenu,
    finishCablePathDrawing,
    cancelCablePathDrawing,
    selectEntity,
    setActiveDrawer,
  ]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isCablePathMode) return;

      if (e.key === 'Escape') {
        if (showContextMenu) {
          hideDrawingContextMenu();
        } else {
          logger.log('Cable path drawing cancelled');
          cancelCablePathDrawing();
          toast.info('Drawing cancelled');
        }
      }
    },
    [isCablePathMode, showContextMenu, hideDrawingContextMenu, cancelCablePathDrawing],
  );

  // Set up event listeners
  useEffect(() => {
    if (!isCablePathMode) return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCablePathMode, containerRef, handleMouseMove, handleClick, handleKeyDown]);

  // Build preview path for SVG
  const previewPath = useMemo(() => {
    const allPoints: Point[] = [];

    // Add start junction if present
    if (startJunctionId && junctionCoordMap) {
      const startJunction = junctionCoordMap.get(startJunctionId);
      if (startJunction) {
        allPoints.push({ x: startJunction.x, y: startJunction.y });
      }
    }

    // Add intermediate points
    allPoints.push(...currentPoints);

    // Add cursor position for the preview line
    if (isDrawing && cursorPos) {
      // Always show line to cursor position (with potential snap)
      if (snapToJunctionId && junctionCoordMap) {
        const snapJunction = junctionCoordMap.get(snapToJunctionId);
        if (snapJunction) {
          allPoints.push({ x: snapJunction.x, y: snapJunction.y });
        } else {
          allPoints.push(cursorPos);
        }
      } else {
        allPoints.push(cursorPos);
      }
    }

    if (allPoints.length < 2) return '';
    const [first, ...rest] = allPoints;
    return `M ${first.x} ${first.y} ${rest.map(p => `L ${p.x} ${p.y}`).join(' ')}`;
  }, [startJunctionId, junctionCoordMap, currentPoints, cursorPos, isDrawing, snapToJunctionId]);

  // Format live length display
  const formattedLength = useMemo(() => {
    if (!scaleData || liveLength === 0) return null;
    return formatMeasurement(liveLength * scaleData.pixelsPerUnit, scaleData);
  }, [liveLength, scaleData]);

  // Don't render if not in cable path mode
  if (!isCablePathMode) return null;

  return (
    <>
      {/* SVG overlay for drawing preview - transformed to match viewport */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          zIndex: 50,
        }}
      >
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
        >
          {/* Preview path line */}
          {previewPath && (
            <path
              d={previewPath}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={3 / zoom}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={`${8 / zoom} ${4 / zoom}`}
              opacity={0.8}
            />
          )}

          {/* Vertex markers */}
          {currentPoints.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r={5 / zoom}
              fill="white"
              stroke="#3b82f6"
              strokeWidth={2 / zoom}
            />
          ))}

          {/* Cursor indicator (when not showing context menu) */}
          {cursorPos && isDrawing && !showContextMenu && (
            <circle
              cx={
                snapToJunctionId && junctionCoordMap?.get(snapToJunctionId)
                  ? junctionCoordMap.get(snapToJunctionId)!.x
                  : cursorPos.x
              }
              cy={
                snapToJunctionId && junctionCoordMap?.get(snapToJunctionId)
                  ? junctionCoordMap.get(snapToJunctionId)!.y
                  : cursorPos.y
              }
              r={6 / zoom}
              fill={snapToJunctionId ? '#22c55e' : '#3b82f6'}
              fillOpacity={0.5}
              stroke={snapToJunctionId ? '#16a34a' : '#2563eb'}
              strokeWidth={2 / zoom}
            />
          )}

          {/* Live length label (when not showing context menu) */}
          {formattedLength && cursorPos && isDrawing && !showContextMenu && (
            <g>
              <rect
                x={cursorPos.x + 15 / zoom}
                y={cursorPos.y - 25 / zoom}
                width={80 / zoom}
                height={24 / zoom}
                rx={4 / zoom}
                fill="white"
                fillOpacity={0.95}
                stroke="#3b82f6"
                strokeWidth={1 / zoom}
              />
              <text
                x={cursorPos.x + 55 / zoom}
                y={cursorPos.y - 8 / zoom}
                textAnchor="middle"
                fill="#3b82f6"
                fontSize={12 / zoom}
                fontWeight={600}
                fontFamily="system-ui, sans-serif"
              >
                {formattedLength}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Context menu for drawing actions */}
      {showContextMenu && contextMenuScreenPosition && (
        <DrawingContextMenu
          screenPosition={contextMenuScreenPosition}
          onAddBox={handleAddBox}
          onEnd={handleEnd}
          isProcessing={isProcessing}
        />
      )}

      {/* Instructions tooltip */}
      {isCablePathMode && !isDrawing && (
        <div
          style={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
          className="bg-black/80 text-white px-4 py-3 rounded-lg text-sm max-w-md text-center"
        >
          <div className="font-medium mb-1">Cable Path Tool</div>
          <div className="text-white/80">
            Click to start drawing. Click adds turning points.
            <br />
            Hold <kbd className="bg-white/20 px-1 rounded">Shift</kbd> for straight lines.
            <br />
            Use <span className="text-blue-400">+ Box</span> to add junction boxes,{' '}
            <span className="text-blue-400">End</span> to finish.
          </div>
        </div>
      )}

      {/* Drawing mode indicator */}
      {isDrawing && !showContextMenu && (
        <div
          style={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
          className="bg-black/60 text-white px-3 py-2 rounded-lg text-xs"
        >
          {currentPoints.length} {currentPoints.length === 1 ? 'point' : 'points'} •
          {isShiftHeld
            ? ' Straight line mode'
            : snapToJunctionId
              ? ' Snapping to junction'
              : ' Click to add vertex'}{' '}
          • Press <kbd className="bg-white/20 px-1 rounded">Esc</kbd> to cancel
        </div>
      )}
    </>
  );
}
